import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { AudioSourceData } from '@lumen5/luminary';

import type { AudioAssetModel } from 'js/src/client';
import {
    getPlayingTrackDuration,
    getPlayingTrackScrubberSeekPercent,
    getPlayingTrackStatus,
    getPlayingTrackUrl,
    setCurrentAudioDetailAndPlay,
    setCurrentAudioPlayingStatus,
} from 'js/src/data/media';
import type { ImmutableMap } from 'js/src/libs/consts';
import { useAppDispatch } from 'js/src/libs/RootState';

export function usePlaybackFunctionality(audio: ImmutableMap<AudioAssetModel | AudioSourceData>) {
    const dispatch = useAppDispatch();

    const audioUrl = audio.get('mp3_url');
    const audioDuration = audio.get('duration');
    if (audioDuration === null) throw new Error('audio duration is null');

    const [isCurrentPlayingTrack, setIsCurrentPlayingTrack] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);

    const playingAudioUrl = useSelector(getPlayingTrackUrl);
    const playingTrackStatus = useSelector(getPlayingTrackStatus);
    const playingTrackDuration = useSelector(getPlayingTrackDuration);
    const playingTrackScrubberSeekPercent = useSelector(getPlayingTrackScrubberSeekPercent);

    useEffect(() => {
        const isCurrentPlayingTrackStatus = audioUrl === playingAudioUrl;
        const isPlayingStatus = isCurrentPlayingTrackStatus && playingTrackStatus;
        setIsCurrentPlayingTrack(isCurrentPlayingTrackStatus);
        setIsPlaying(isPlayingStatus);
    }, [audioUrl, playingAudioUrl, playingTrackStatus]);

    const pause = useCallback(() => {
        dispatch(setCurrentAudioPlayingStatus({ isPlaying: false }));
    }, [dispatch]);

    const play = useCallback(() => {
        let seekPercent = 0;
        if (isCurrentPlayingTrack) {
            seekPercent = parseFloat(((playingTrackDuration / audioDuration) * 100).toFixed(2));
        }
        dispatch(
            setCurrentAudioDetailAndPlay(
                audioUrl,
                seekPercent,
                true,
                seekPercent,
                (audioDuration * seekPercent) / 100,
            ),
        );
    }, [isCurrentPlayingTrack, dispatch, audioUrl, playingTrackDuration, audioDuration]);

    const togglePlayPause = useCallback(() => {
        if (isCurrentPlayingTrack && isPlaying) {
            pause();
        } else {
            play();
        }
    }, [isCurrentPlayingTrack, isPlaying, pause, play]);

    const seek = useCallback(
        (seekPercent: number) => {
            dispatch(
                setCurrentAudioDetailAndPlay(
                    audioUrl,
                    seekPercent,
                    true,
                    seekPercent,
                    (audioDuration * seekPercent) / 100,
                ),
            );
        },
        [audioDuration, audioUrl, dispatch],
    );

    return {
        play,
        pause,
        togglePlayPause,
        seek,
        isCurrentPlayingTrack,
        isPlaying,
        currentTimePercentage: playingTrackScrubberSeekPercent,
    };
}
